/**
 * DensityControl (docs/04 §5) — a segmented control, not a toggle.
 *
 * The first version was a single button labelled with the CURRENT mode, which
 * is the classic ambiguity: "Comfortable" could equally mean "you are
 * comfortable" or "press for comfortable", and there was no way to tell which
 * without pressing it. A segmented control shows both options and marks the
 * live one, so the state and the choice are the same glance.
 *
 * Built from two `aria-pressed` toggle buttons in a labelled group rather than
 * a roving-tabindex radiogroup: both options stay reachable by Tab with no
 * focus bookkeeping to get wrong, and a screen reader announces each as
 * "Compact, toggle button, pressed" — accurate without a custom keyboard model.
 */

import { type Density } from '@propos/shared';
import { clsx } from 'clsx';
import { Rows2, Rows4 } from 'lucide-react';
import type { ReactNode } from 'react';

const OPTIONS: readonly {
  readonly value: Density;
  readonly label: string;
  readonly icon: ReactNode;
}[] = [
  { value: 'comfortable', label: 'Comfortable', icon: <Rows2 size={15} aria-hidden /> },
  { value: 'compact', label: 'Compact', icon: <Rows4 size={15} aria-hidden /> },
];

export function DensityControl({
  density,
  onChange,
}: {
  readonly density: Density;
  readonly onChange: (next: Density) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label="Row density"
      // Where this control appears is the caller's decision, not the
      // component's: the shell hides it below the table breakpoint, the design
      // gallery shows it at every width.
      className="flex shrink-0 rounded-ctl border border-card-line bg-canvas p-0.5"
    >
      {OPTIONS.map((option) => {
        const active = option.value === density;
        return (
          <button
            key={option.value}
            type="button"
            // The visible label is hidden below the labelled-sidebar band, so
            // the accessible name has to come from aria-label — otherwise the
            // control is two nameless icon buttons on a tablet, which is what
            // the axe pass caught.
            aria-label={option.label}
            aria-pressed={active}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
            className={clsx(
              'inline-flex min-h-9 items-center gap-1.5 rounded-[6px] px-2.5 text-meta font-medium',
              'transition-colors duration-(--dur-fast)',
              'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-700',
              active ? 'bg-card text-ink shadow-sm' : 'text-ink2 hover:text-ink',
            )}
          >
            {option.icon}
            <span aria-hidden className="hidden md:inline">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
